import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { Modal, Button, Table, SearchBox, Transfer } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import defaultLogo from '@/pages/dashboard/assets/img/svg/gameError.svg';
import '@/pages/dashboard/assets/scss/components/navigation/editRelatedGame.scss';
import { IS_GLOBAL } from '@home/_config/config';

const { selectable, removeable, scrollable, draggable, autotip } = Table.addons;
let delList: any[] = [];

interface GameItemType {
  cover: string;
  game_List?: GameItemType[];
  game_cn_name: string;
  game_code: string;
  game_name: string;
  is_access: boolean;
  role_id: string;
}

export default function EditRelatedGame(props: any) {
  const {
    visible,
    close,
    followList = []
  } = props;
  const { formatMessage } = useIntl();
  const {
    gameList: allGameList,
    currentGame
  } = useSelector((state: any) => {
    const { currentGame, gameList } = state.dashboard;
    return {
      gameList,
      currentGame
    };
  });

  const gameTypeIcon = (cvm: { game_type: string | string[]; })=>{
    if (cvm?.game_type?.includes('mobile') && !cvm.game_type?.includes('pc')){
      return 'other-icon';
    }
    if (cvm?.game_type?.includes('pc')){
      return 'monitor';
    }
    if (cvm?.game_type === 'console'){
      return 'merit';
    }
  };

  const columns = [
    {
      key: 'instance',
      className: 'tea-text-overflow',
      header: <div className='dbd-edit-related-title'>{formatMessage({ id: 'game_selector_game_table_header' })}</div>,
      render: (cvm: any) => (
        <div className='tableBox'>
          <img src={cvm.cover || defaultLogo} className='tableBox-img' />
          <div className='tableBox-right'>
            <div className='tableBox-title'>{cvm.game_name}</div>
            <div className='tableBox-desc'>
              {
                cvm?.game_type !== 'none' && <span className='tableBox-desc-line'>
                  <SvgIcon className='tableBox-desc-icon' type={gameTypeIcon(cvm)} />
                </span>
              }
              {cvm.game_cn_name}
            </div>
          </div>
        </div>
      ),
    },
  ];
  // 外部操作的收藏列表
  const dispatch = useDispatch();
  const outSideFollowList = followList?.map((item: any) => item.game_code) ?? [];
  const [selectCodeList, setSelectCodeList] = useState<any[]>(outSideFollowList);
  const [tempFollowList, setTempFollowList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);

  const isShowGameList = useMemo(() => {
    if (!allGameList) return [];
    const reg = new RegExp(searchValue?.toLowerCase());
    return allGameList.filter((game: GameItemType) => {
      // GLOBAL环境编辑我的收藏，下拉选项过滤掉无权限游戏
      if ( IS_GLOBAL && !game.is_access) return false; 
      return (
        game.game_name?.toLowerCase()?.match(reg) ||
        game.game_cn_name?.toLowerCase()?.match(reg)
      );
    });
  }, [allGameList, searchValue]);

  const handleClose = (close?: any) => {
    close?.();
    delList = [];
  };

  const handleConfirm = (close?: any) => {
    setLoading(true);
    const delGameList = delList?.map(gameCode => ({
      ...(allGameList?.find((game: any) => game?.game_code === gameCode) ?? {}),
      is_del: true
    }));

    // 收藏列表为空,编辑收藏游戏为空点击提交（不需要请求接口）
    if (delGameList?.length === 0 && tempFollowList?.length === 0) {
      setLoading(false);
      handleClose(close);
      return;
    }
    dispatch({
      type: 'dashboard/batchSaveFollow',
      payload: {
        game_code: currentGame.game_code,
        data: tempFollowList.concat(delGameList)?.map((game: any) => ({
          id: game.followId,
          game_code: game.game_code,
          is_del: game.is_del ? 1 : 0
        }))
      }
    })
      .then(() => {
        setLoading(false);
        handleClose(close);
      }, () => {
      }).finally(() => {
      });
  };

  useEffect(() => {
    setTempFollowList(selectCodeList?.map(gameCode => allGameList?.find((item: any) => item?.game_code === gameCode)));
  }, [selectCodeList, allGameList]);

  useEffect(() => {
    setSelectCodeList(outSideFollowList);
  }, [JSON.stringify(outSideFollowList)]);

  return (
    <Modal
      disableCloseIcon
      size='xl'
      className='editRelatedGame'
      visible={visible}
      caption={(
        <div className='editRelatedGame-black'>
          {/* <div onClick={() => handleClose(close)}><SvgIcon className='editRelatedGame-blackicon' type='left-pointing' /></div> */}
          {formatMessage({ id: 'game_selector_edit_title' })}
        </div>
      )}
      onClose={close}
    >
      <Modal.Body>
        <Transfer
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title={formatMessage({ id: 'game_selector_edit_all_game' })}
              header={
                <SearchBox
                  className='edit-search-box'
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder={formatMessage({ id: 'game_selector_search_text' })}
                />
              }
            >
              <Table
                className='editGameTable'
                records={isShowGameList}
                recordKey='game_code'
                rowDisabled={record => record.status === 'stopped'}
                rowClassName={record => record.status}
                columns={columns}
                addons={[
                  scrollable({
                    maxHeight: 528,
                  }),
                  selectable({
                    value: selectCodeList,
                    onChange: (list, record) => {
                      const { checked, recordKey } = record;
                      if (checked) {
                        delList = delList?.filter(v => v !== recordKey);
                        setSelectCodeList([...selectCodeList, recordKey]);
                      } else {
                        delList.push(recordKey);
                        setSelectCodeList(selectCodeList?.filter(v => v !== recordKey));
                      }
                    },
                    rowSelect: true,
                  }),
                  autotip({
                    isFound: isShowGameList?.length === 0, // 是否显示
                    foundText: formatMessage({ id: 'common_search_no_data' }),
                    style: { lineHeight: '526px',border: 'none' },
                  }),
                ]}
              />
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              scrollable={false}
              title={`${formatMessage({ id: 'game_selector_edit_select_game' })} (${selectCodeList?.length || 0})`}
            >
              {
                useMemo(() => (
                  <Table
                    records={tempFollowList}
                    recordKey='game_code'
                    columns={columns}
                    className="tableBox-table"
                    addons={[
                      scrollable({
                        maxHeight: 528,
                      }),
                      removeable({
                        onRemove: (key: any) => {
                          if (followList?.some((game: any) => game.game_code === key)) {
                            delList.push(key);
                          }
                          setSelectCodeList(selectCodeList?.filter(gameCode => gameCode !== key));
                        }
                      }),
                      draggable({
                        onDragEnd: (sortList: any) => {
                          setSelectCodeList(sortList?.map((item: { game_code: string; }) => item?.game_code));
                          setTempFollowList(sortList);
                        }
                      }),
                    ]}
                  />
                ), [tempFollowList, selectCodeList])
              }
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Button loading={loading} type='primary' onClick={() => handleConfirm(close)}>
          {formatMessage({ id: 'game_selector_confirm' })}
        </Button>
        <Button type='weak' onClick={() => handleClose(close)}>
          {formatMessage({ id: 'game_selector_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};